import React, { useCallback, useState } from 'react';
import { useGetIdentity, useNotify } from 'react-admin';
import moment from 'moment';
import {
  Check,
  ChevronDown,
  ChevronRight,
  Clock,
  Loader2,
  Mail,
  MessageSquare,
  Phone,
  PhoneMissed,
  Search,
  User,
  Voicemail,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ConversationScope,
  InboxChannel,
  InboxItem,
  contactLabel,
  conversationContext,
  conversationPreview,
  conversationScope,
  rowTime,
  stageLabel,
  stageTone,
} from './types';
import { InboxFilters, OwnerScope, RegionFilter } from './useInboxItems';
import { laterToday, markDone, snooze, tomorrowMorning } from './inboxApi';

// ─── Filter bar ──────────────────────────────────────────────────────────────

function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex gap-0.5 rounded-lg bg-muted p-0.5">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={value === o.value}
          className={cn(
            'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
            value === o.value
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors',
        active
          ? 'border-primary/30 bg-primary/10 text-primary'
          : 'border-border bg-background text-muted-foreground hover:border-muted-foreground/40',
      )}
    >
      {children}
    </button>
  );
}

export function InboxFilterBar({
  filters,
  onChange,
}: {
  filters: InboxFilters;
  onChange: (next: InboxFilters) => void;
}) {
  const toggleChannel = (c: InboxChannel) => {
    const next = new Set(filters.channels);
    if (next.has(c)) next.delete(c);
    else next.add(c);
    onChange({ ...filters, channels: next });
  };

  return (
    <div className="flex flex-col gap-2 border-b px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Segmented<OwnerScope>
          value={filters.ownerScope}
          onChange={(v) => onChange({ ...filters, ownerScope: v })}
          options={[
            { value: 'mine', label: 'Mine' },
            { value: 'unclaimed', label: 'Unclaimed' },
            { value: 'all', label: 'All' },
          ]}
        />
        <Segmented<RegionFilter>
          value={filters.region}
          onChange={(v) => onChange({ ...filters, region: v })}
          options={[
            { value: 'all', label: 'All' },
            { value: 'quebec', label: 'QC' },
            { value: 'ontario', label: 'ON' },
          ]}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Chip
          active={filters.needsReplyOnly}
          onClick={() => onChange({ ...filters, needsReplyOnly: !filters.needsReplyOnly })}
        >
          {/* A toggle, deliberately without a number. The only count shown
              anywhere is the shared team-wide one in the header — a second
              figure here would be a count of a different thing wearing the
              same badge. */}
          Needs reply
        </Chip>
        <Chip active={filters.channels.has('sms')} onClick={() => toggleChannel('sms')}>
          <MessageSquare className="h-3 w-3" /> SMS
        </Chip>
        <Chip active={filters.channels.has('call')} onClick={() => toggleChannel('call')}>
          <Phone className="h-3 w-3" /> Calls
        </Chip>
        <Chip active={filters.channels.has('email')} onClick={() => toggleChannel('email')}>
          <Mail className="h-3 w-3" /> Email
        </Chip>
        <Chip
          active={!!filters.unmatchedOnly}
          onClick={() => onChange({ ...filters, unmatchedOnly: !filters.unmatchedOnly })}
        >
          Unmatched
        </Chip>
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Search name, phone, message…"
          className="h-8 pl-8 text-xs"
        />
        {filters.search && (
          <button
            type="button"
            onClick={() => onChange({ ...filters, search: '' })}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear search"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

/**
 * Customer or lead, at a glance: the avatar tint. Amber is kept for "needs a
 * reply" (the dot and the time), so a lead that needs a reply and a customer
 * that needs a reply stay tellable apart.
 */
const SCOPE_AVATAR: Record<ConversationScope, string> = {
  // A real case. The brand green is the "this is a customer" colour.
  funeral: 'bg-primary/10 text-primary',
  // Still a lead — nobody has a case yet.
  lead: 'bg-violet-100 text-violet-700',
  // A person we know, attached to neither. Deliberately colourless.
  client: 'bg-muted text-muted-foreground',
};

const SCOPE_TITLE: Record<ConversationScope, string> = {
  funeral: 'Customer — has a case',
  lead: 'Lead',
  client: 'No case or lead',
};

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span
      className={cn(
        'shrink-0 whitespace-nowrap rounded-full px-2 py-px text-[11px] font-semibold leading-normal',
        className,
      )}
    >
      {children}
    </span>
  );
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function ChannelIcon({ item, className }: { item: InboxItem; className?: string }) {
  const cls = cn('shrink-0', className);
  if (item.last_message_channel === 'email') {
    return <Mail className={cn(cls, 'text-primary')} />;
  }
  if (item.last_message_channel === 'voicemail') {
    return <Voicemail className={cn(cls, 'text-rose-600')} />;
  }
  if (item.last_message_channel === 'call') {
    return item.last_call_status === 'answered' ? (
      <Phone className={cn(cls, 'text-blue-600')} />
    ) : (
      <PhoneMissed className={cn(cls, 'text-rose-600')} />
    );
  }
  return <MessageSquare className={cn(cls, 'text-primary')} />;
}

/**
 * Who, what kind and by which channel, in one circle: initials (or a person
 * glyph for a bare number) tinted by case / lead / neither, with the channel
 * badged on. Shared by the dock row and the message toast so they read alike.
 */
export function ConversationAvatar({ item }: { item: InboxItem }) {
  const scope = conversationScope(item);
  const { label, named } = contactLabel(item);
  return (
    <div
      className={cn(
        'relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[15px] font-bold',
        SCOPE_AVATAR[scope],
      )}
      title={SCOPE_TITLE[scope]}
    >
      {named ? initials(label) : <User className="h-5 w-5" />}
      <span className="absolute -bottom-0.5 -right-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-background ring-2 ring-background">
        <ChannelIcon item={item} className="h-3 w-3" />
      </span>
    </div>
  );
}

function ConversationRow({
  item,
  selected,
  arrived,
  onSelect,
  onChanged,
}: {
  item: InboxItem;
  selected: boolean;
  arrived: boolean;
  onSelect: () => void;
  onChanged: () => void;
}) {
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const actor = { id: identity?.id as string | undefined, name: identity?.fullName };

  const handleDone = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await markDone(item.conversation_key, actor);
        notify('Marked done', { type: 'info' });
        onChanged();
      } catch (err: any) {
        notify(err?.message || 'Could not mark it done', { type: 'warning' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.conversation_key, identity?.id, identity?.fullName, notify, onChanged],
  );

  const handleSnooze = useCallback(
    async (until: Date, label: string) => {
      try {
        await snooze(item.conversation_key, until, actor);
        notify(`Snoozed until ${label}`, { type: 'info' });
        onChanged();
      } catch (err: any) {
        notify(err?.message || 'Could not snooze it', { type: 'warning' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.conversation_key, identity?.id, identity?.fullName, notify, onChanged],
  );

  const context = conversationContext(item);
  const scope = conversationScope(item);
  const { label } = contactLabel(item);
  const preview = conversationPreview(item);
  const unreplied = item.is_unreplied;

  return (
    <li
      onClick={onSelect}
      data-conversation-key={item.conversation_key}
      className={cn(
        'group relative flex cursor-pointer items-start gap-2.5 py-2.5 pl-1.5 pr-3 transition-colors',
        selected ? 'bg-accent' : 'hover:bg-muted/60',
        arrived && 'animate-in fade-in slide-in-from-top-2 duration-500',
      )}
    >
      {/* Needs a reply: the dot, a bolder name and an amber time. */}
      <span
        className={cn(
          'mt-[18px] h-2 w-2 shrink-0 rounded-full',
          unreplied ? 'bg-amber-500' : 'bg-transparent',
        )}
        title={unreplied ? 'Needs reply' : undefined}
      />

      <ConversationAvatar item={item} />

      <div className="flex min-w-0 flex-1 flex-col gap-px">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'truncate text-[15px] text-foreground',
              unreplied ? 'font-bold' : 'font-semibold',
            )}
          >
            {label}
          </span>
          {item.language && (
            <span className="shrink-0 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              {item.language}
            </span>
          )}
          <span
            className={cn(
              'ml-auto flex shrink-0 items-center gap-1 text-[13px] tabular-nums',
              unreplied ? 'font-bold text-amber-700' : 'text-muted-foreground',
            )}
            title={
              item.snoozed_until
                ? `Snoozed until ${moment(item.snoozed_until).calendar()}`
                : moment(item.last_message_at).format('LLLL')
            }
          >
            {item.snoozed_until && <Clock className="h-3.5 w-3.5" />}
            {rowTime(item.last_message_at)}
          </span>
        </div>

        {scope === 'funeral' && (
          <div className="flex items-center gap-2">
            <span className="truncate text-[13px] font-medium text-foreground/80">
              Re: {context}
            </span>
            <Pill className={cn('ml-auto', stageTone(item.cremation_status))}>
              {stageLabel(item.cremation_status)}
            </Pill>
          </div>
        )}
        {scope === 'client' && !item.is_unmatched && (
          <span className="truncate text-[13px] font-medium text-muted-foreground">
            Not linked to a case
          </span>
        )}

        {/* A lead has no case line, so its pill rides on the preview's first
            line — the same spot under the time where a case's stage pill sits. */}
        <div className="flex items-start gap-2">
          <span
            className={cn(
              'line-clamp-2 min-w-0 flex-1 text-sm',
              unreplied ? 'text-foreground' : 'text-foreground/75',
            )}
          >
            {preview.prefix && <span className="text-muted-foreground">{preview.prefix} </span>}
            {preview.emphasis && <span className="font-semibold">{preview.emphasis}</span>}
            {preview.text}
          </span>
          {scope === 'lead' && <Pill className={cn('mt-px', SCOPE_AVATAR.lead)}>Lead</Pill>}
          {item.is_unmatched && <Pill className={cn('mt-px', SCOPE_AVATAR.client)}>Unmatched</Pill>}
        </div>
      </div>

      <div className="absolute bottom-1.5 right-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          type="button"
          onClick={(e) => void handleDone(e)}
          title="Mark done"
          className="flex h-6 w-6 items-center justify-center rounded-md border bg-background text-muted-foreground shadow-sm hover:border-primary/40 hover:text-primary"
        >
          <Check className="h-3.5 w-3.5" />
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              title="Snooze"
              className="flex h-6 w-6 items-center justify-center rounded-md border bg-background text-muted-foreground shadow-sm hover:border-primary/40 hover:text-primary"
            >
              <Clock className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="cleo-ui inbox-menu" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => void handleSnooze(laterToday(), 'later today')}>
              Later today
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => void handleSnooze(tomorrowMorning(), 'tomorrow 9am')}
            >
              Tomorrow morning
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </li>
  );
}

// ─── List ────────────────────────────────────────────────────────────────────

export function ConversationList({
  items,
  isLoading,
  error,
  selectedKey,
  arrivedIds,
  onSelect,
  onChanged,
}: {
  items: InboxItem[];
  isLoading: boolean;
  error: string | null;
  selectedKey: string | null;
  arrivedIds: Set<string>;
  onSelect: (item: InboxItem) => void;
  onChanged: () => void;
}) {
  if (isLoading && items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 py-10 text-center">
        <p className="text-sm text-muted-foreground">{error}</p>
        <button
          type="button"
          onClick={onChanged}
          className="text-xs font-medium text-primary hover:underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 py-10 text-center text-sm text-muted-foreground">
        Nothing here. Widen the filters to see more.
      </div>
    );
  }

  // Quiet IVR menu hang-ups (T-058): an unknown, non-ad number that hung up
  // before picking a market, once. Not needs-reply, so they wait in one
  // collapsed group instead of mixing with the conversations that do.
  const menuHangups = items.filter((i) => i.is_menu_hangup);
  const rest = menuHangups.length ? items.filter((i) => !i.is_menu_hangup) : items;

  return (
    <ul className="flex-1 divide-y divide-border/60 overflow-y-auto overscroll-contain">
      {menuHangups.length > 0 && (
        <MenuHangupGroup
          items={menuHangups}
          selectedKey={selectedKey}
          arrivedIds={arrivedIds}
          onSelect={onSelect}
          onChanged={onChanged}
        />
      )}
      {rest.map((item) => (
        <ConversationRow
          key={item.id}
          item={item}
          selected={item.conversation_key === selectedKey}
          arrived={arrivedIds.has(item.id)}
          onSelect={() => onSelect(item)}
          onChanged={onChanged}
        />
      ))}
    </ul>
  );
}

// ─── "Hung up in menu" ───────────────────────────────────────────────────────

/**
 * Callers we don't know who hung up in the phone menu before picking a market,
 * from a non-ad number, once (T-058). No lead was created for them and they
 * don't count as needing a reply; a second call, a text, or a staff callback
 * turns them into a normal conversation. Collapsed by default, newest first,
 * one tap to open (and call back from the thread), one to clear them all.
 */
function MenuHangupGroup({
  items,
  selectedKey,
  arrivedIds,
  onSelect,
  onChanged,
}: {
  items: InboxItem[];
  selectedKey: string | null;
  arrivedIds: Set<string>;
  onSelect: (item: InboxItem) => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const Chevron = open ? ChevronDown : ChevronRight;

  const dismissAll = async () => {
    setBusy(true);
    try {
      const actor = { id: identity?.id as string | undefined, name: identity?.fullName };
      await Promise.all(items.map((i) => markDone(i.conversation_key, actor)));
      notify(`Cleared ${items.length} menu hang-up${items.length === 1 ? '' : 's'}`, { type: 'info' });
      onChanged();
    } catch (err: any) {
      notify(err?.message || 'Could not clear them', { type: 'warning' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="bg-muted/30" data-testid="menu-hangup-group">
      <div className="flex items-center gap-2 py-2 pl-3 pr-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
        >
          <Chevron className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <PhoneMissed className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-[13px] font-semibold text-foreground/80">
            Hung up in menu
          </span>
          <span className="shrink-0 rounded-full bg-muted px-1.5 text-[11px] font-semibold tabular-nums text-muted-foreground">
            {items.length}
          </span>
        </button>
        {open && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void dismissAll()}
            className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Clear all
          </button>
        )}
      </div>
      {open && (
        <>
          <p className="px-3 pb-1.5 text-[11px] leading-snug text-muted-foreground">
            Unknown numbers that hung up before picking a market. Not in Needs reply.
          </p>
          <ul className="divide-y divide-border/60 border-t border-border/60 bg-background">
            {items.map((item) => (
              <ConversationRow
                key={item.id}
                item={item}
                selected={item.conversation_key === selectedKey}
                arrived={arrivedIds.has(item.id)}
                onSelect={() => onSelect(item)}
                onChanged={onChanged}
              />
            ))}
          </ul>
        </>
      )}
    </li>
  );
}
